import API from '@aws-amplify/api'
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert'
import makeStyles from '@mui/styles/makeStyles';
import { DatePicker } from '@material-ui/pickers'
import { PurchaseOrder, PurchaseOrderItem } from 'interfaces/IPurchaseOrder'
import React from 'react'
import { useEffect, useState } from 'react'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { TYPE_PACKAGING } from './purchaseOrderForm'
import { useSelector } from 'react-redux'
import { IReduxState } from 'interfaces/IReduxState'

const useStyles = makeStyles((theme) => ({
  expandableDetails: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  hiddenDetails: {
    paddingTop: 0,
  },
  productRow: {
    cursor: 'pointer',
  },
}))
export default function PendingPurchaseOrders({
  type,
  department,
}: {
  type: string
  department?: string
}) {
  const classes = useStyles()
  const [receivedQuantity, setReceivedQuantity] = useState<number | string | null>(
    null
  )
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([])
  const [selectedPurchaseOrderItem, setSelectedPurchaseOrderItem] =
    React.useState<PurchaseOrderItem | null>(null)
  const [isConfirmingReceivedDialogOpen, setIsConfirmingReceivedDialogOpen] =
    React.useState(false)
  const user = useSelector((state: IReduxState) => state.currentUser || {})
  const [receivedDate, setReceivedDate] = useState<Date | null>(new Date())
  const [receivedNotes, setReceivedNotes] = useState('')
  const [itemHistory, setItemHistory] = useState<any[]>([])
  const [isHistoryLoading, setIsHistoryLoading] = useState(false)
  const [isSuccessSnackbarOpen, setIsSuccessSnackbarOpen] =
    React.useState(false)
  const [isFailureSnackbarOpen, setIsFailureSnackbarOpen] =
    React.useState(false)
  const [alertMessage, setAlertMessage] = React.useState('')

  const handleSnackbarClose = (): void => {
    setIsFailureSnackbarOpen(false)
    setIsSuccessSnackbarOpen(false)
  }

  const getPurchaseOrders = React.useCallback(async () => {
    setIsLoading(true)

    const response = await API.get(
      'products',
      '/purchaseOrders/getPurchaseOrder',
      {
        queryStringParameters: {
          type,
          status: 'PENDING',
          department,
        },
      }
    )
    const purchaseOrders = response
      .map((purchaseOrder: any) => new PurchaseOrder(purchaseOrder))
      .sort((a: any, b: any) => {
        const dateA = a.poDate ? new Date(a.poDate).getTime() : 0
        const dateB = b.poDate ? new Date(b.poDate).getTime() : 0
        return dateB - dateA // descending, newest first
      })
    setPurchaseOrders(purchaseOrders)
    setIsLoading(false)
  }, [type, department])

  const [expandedRows, setExpandedRows] = React.useState<{
    [key: string]: boolean
  }>(
    purchaseOrders.reduce<{
      [key: string]: boolean
    }>((acc, purchaseOrder) => {
      acc[purchaseOrder.purchaseOrderId || ''] = false
      return acc
    }, {})
  )

  const handleMarkItemReceived = async () => {
    setIsLoading(true)
    try {
      await API.post(
        'products',
        `/purchaseOrders/markItemReceived/${selectedPurchaseOrderItem?.purchaseOrderItemId}`,
        {
          body: {
            receivedQuantity: Number(receivedQuantity),
            receivedDate,
            receivedNotes,
          },
        }
      )

      await getPurchaseOrders()
      setIsConfirmingReceivedDialogOpen(false)
      setReceivedQuantity(null)
      setReceivedDate(new Date())
      setReceivedNotes('')
      setSelectedPurchaseOrderItem(null)
      setAlertMessage('Berhasil menandai item diterima.')
      setIsSuccessSnackbarOpen(true)
    } catch (error: any) {
      console.error(error)
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Gagal menandai item diterima.'
      setAlertMessage(errorMessage)
      setIsFailureSnackbarOpen(true)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    getPurchaseOrders()
  }, [getPurchaseOrders])

  useEffect(() => {
    if (isConfirmingReceivedDialogOpen && selectedPurchaseOrderItem) {
      const fetchHistory = async () => {
        setIsHistoryLoading(true)
        try {
          const result = await API.get(
            'products',
            `/purchaseOrders/purchaseOrderItemHistory/${selectedPurchaseOrderItem.purchaseOrderId}`,
            {}
          )
          // Filter for this item only
          setItemHistory(
            result.filter(
              (h: any) =>
                h.purchaseOrderItemId ===
                selectedPurchaseOrderItem.purchaseOrderItemId
            )
          )
        } catch {
          setItemHistory([])
        }
        setIsHistoryLoading(false)
      }
      fetchHistory()
    } else {
      setItemHistory([])
    }
  }, [isConfirmingReceivedDialogOpen, selectedPurchaseOrderItem])

  const [isLoading, setIsLoading] = React.useState(true)

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="none"></TableCell>
              <TableCell>PO Number</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell>Supplier</TableCell>
              <TableCell>Tanggal PO</TableCell>
              <TableCell>Tanggal PO Diinput</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {purchaseOrders.length === 0 && !isLoading && (
              <TableRow>
                <TableCell colSpan={12}>
                  <Typography>Tidak ada PO untuk diproses. </Typography>
                </TableCell>
              </TableRow>
            )}
            {purchaseOrders.map((purchaseOrder) => (
              <React.Fragment key={purchaseOrder.purchaseOrderId || ''}>
                <TableRow
                  className={classes.productRow}
                  hover
                  onClick={(e) => {
                    setExpandedRows({
                      ...expandedRows,
                      [purchaseOrder.purchaseOrderId ?? 0]:
                        !expandedRows[purchaseOrder.purchaseOrderId ?? 0],
                    })
                  }}
                >
                  <TableCell padding="none">
                    <IconButton aria-label="expand row" size="small">
                      {expandedRows[purchaseOrder.purchaseOrderId ?? 0] ? (
                        <KeyboardArrowUpIcon />
                      ) : (
                        <KeyboardArrowDownIcon />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell>{purchaseOrder.poNumber || '-'}</TableCell>
                  <TableCell
                    style={{
                      maxWidth: 300,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {purchaseOrder.notes || '-'}
                  </TableCell>
                  <TableCell>{purchaseOrder.supplier}</TableCell>
                  <TableCell>
                    {purchaseOrder.poDate?.toLocaleDateString()}
                  </TableCell>

                  <TableCell>
                    {purchaseOrder.createdAt?.toLocaleDateString()}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell
                    colSpan={12}
                    className={
                      expandedRows[purchaseOrder.purchaseOrderId ?? 0]
                        ? classes.hiddenDetails
                        : classes.expandableDetails
                    }
                  >
                    <Collapse
                      in={expandedRows[purchaseOrder.purchaseOrderId ?? 0]}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box>
                        <TableContainer>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>Kode Item</TableCell>
                                <TableCell>Nama Item</TableCell>
                                <TableCell>Notes</TableCell>
                                <TableCell>Qty Dibeli</TableCell>
                                <TableCell>Qty Diterima</TableCell>
                                <TableCell></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {purchaseOrder.purchaseOrderItems?.map((item) => (
                                <TableRow>
                                  <TableCell>{item.itemId}</TableCell>
                                  <TableCell>{item.itemName}</TableCell>
                                  <TableCell>{item.notes || '-'}</TableCell>
                                  <TableCell>
                                    {item.quantityPurchased}
                                  </TableCell>
                                  <TableCell>
                                    {item.quantityReceived ?? 0}
                                  </TableCell>
                                  <TableCell>
                                    <Button
                                      variant="contained"
                                      onClick={() => {
                                        setSelectedPurchaseOrderItem(item)
                                        setIsConfirmingReceivedDialogOpen(true)
                                      }}
                                      color="primary"
                                    >
                                      Tandai Terima
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        {purchaseOrder.notes && (
                          <Box mt={2}>
                            <Typography>
                              Notes PO: {purchaseOrder.notes}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog
        fullWidth
        maxWidth="xl"
        open={isConfirmingReceivedDialogOpen}
        onClose={() => setIsConfirmingReceivedDialogOpen(false)}
      >
        <DialogTitle>Tandai Item Diterima</DialogTitle>
        <DialogContent>
          <List>
            <ListItem>Kode: {selectedPurchaseOrderItem?.itemId}</ListItem>
            <ListItem>Nama: {selectedPurchaseOrderItem?.itemName}</ListItem>
            <ListItem>
              Qty Dibeli: {selectedPurchaseOrderItem?.quantityPurchased}
              {type === TYPE_PACKAGING ? 'pcs' : 'kg'}
            </ListItem>
            <ListItem>
              <Box width="100%">
                <Typography variant="subtitle1">
                  Riwayat Penerimaan Item
                </Typography>
                {isHistoryLoading ? (
                  <Typography>Loading...</Typography>
                ) : itemHistory.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Qty Diterima</TableCell>
                          <TableCell>Total Qty Diterima</TableCell>
                          <TableCell>Qty Diterima Sebelumnya</TableCell>
                          <TableCell>Tanggal Diterima</TableCell>
                          <TableCell>Catatan</TableCell>
                          <TableCell>Diterima Oleh</TableCell>
                          <TableCell>Created At</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {itemHistory.map((h) => (
                          <TableRow key={h.receiptHistoryId}>
                            <TableCell>{h.receivedQuantity}</TableCell>
                            <TableCell>
                              {h.totalReceivedQuantity ?? '-'}
                            </TableCell>
                            <TableCell>
                              {h.prevReceivedQuantity ?? '-'}
                            </TableCell>
                            <TableCell>
                              {h.receivedDate
                                ? new Date(h.receivedDate).toLocaleDateString(
                                  'id-ID'
                                )
                                : '-'}
                            </TableCell>
                            <TableCell>{h.receivedNotes || '-'}</TableCell>
                            <TableCell>{h.username || '-'}</TableCell>
                            <TableCell>
                              {h.createdAt
                                ? new Date(h.createdAt).toLocaleString('id-ID')
                                : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography color="textSecondary">
                    tidak ada sejarah penerimaan item
                  </Typography>
                )}
              </Box>
            </ListItem>
            <ListItem>
              <DatePicker
                fullWidth
                label="Tanggal Diterima"
                value={receivedDate}
                onChange={setReceivedDate}
                format="dd/MM/yyyy"
                inputVariant="outlined"
                style={{ width: '100%' }}
              />
            </ListItem>
            <ListItem>Diterima oleh: {user.username || '-'}</ListItem>
            <ListItem>
              <TextField
                fullWidth
                type="number"
                inputProps={{
                  step: type === TYPE_PACKAGING ? '1' : '0.001',
                  min: 0,
                }}
                label={`Jumlah Diterima (${type === TYPE_PACKAGING ? 'pcs' : 'kg'
                  })`}
                value={receivedQuantity ?? ''}
                onChange={(e) => setReceivedQuantity(e.target.value)}
                error={receivedQuantity !== null && receivedQuantity <= 0}
                helperText={
                  receivedQuantity === null || receivedQuantity <= 0
                    ? 'Quantity harus diisi terlebih dahulu'
                    : ''
                }
              />
            </ListItem>
            <ListItem>
              <TextField
                fullWidth
                label="Catatan Diterima"
                value={receivedNotes}
                onChange={(e) => setReceivedNotes(e.target.value)}
              />
            </ListItem>
            <ListItem>
              <Button
                variant="contained"
                color="primary"
                disabled={!receivedQuantity || isLoading}
                onClick={handleMarkItemReceived}
              >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Save'}
              </Button>
            </ListItem>
          </List>
        </DialogContent>
      </Dialog>
      <Snackbar
        open={isSuccessSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="success" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
      <Snackbar
        open={isFailureSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="error" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
    </Paper>
  )
}
